import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSS } from '@dnd-kit/utilities';
import { TId, TPlantWithAccessControl } from './user-plant-access-management.tsx';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';

export type TColumn = {
  id: TId;
  title: string;
};

const defaultCols: TColumn[] = [
  {
    id: 'available-plants',
    title: 'Available plants',
  },
  {
    id: 'associated-plants',
    title: 'Associated plants',
  },
];

function PlantCard({ plant }: { plant: TPlantWithAccessControl }) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable(
    {
      id: plant.plantId,
      data: {
        type: 'Plant',
        plant,
      },
    }
  );

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="relative flex cursor-grab justify-between rounded-lg bg-slate-100 px-4 py-2 hover:ring-2 hover:ring-inset hover:ring-primary aria-disabled:opacity-50 aria-disabled:hover:ring-0"
      />
    );
  }

  return (
    <div
      title={`${plant.basicDetails.name}`}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative flex cursor-grab justify-between rounded-lg bg-slate-100 px-4 py-2 hover:ring-2 hover:ring-inset hover:ring-primary aria-disabled:opacity-50 aria-disabled:hover:ring-0"
    >
      <span className="my-auto w-full select-none overflow-y-auto overflow-x-hidden whitespace-pre-wrap">
        {`${plant.basicDetails.name} `}
        {plant.newlyAdded && (
          <span className="inline-block size-2 rounded-full bg-blue-400"></span>
        )}
      </span>
      <button>
        <svg viewBox="0 0 20 20" width="12">
          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
        </svg>
      </button>
    </div>
  );
}

function ColumnContainer({
  column,
  plants,
}: {
  column: TColumn;
  plants: TPlantWithAccessControl[];
}) {
  const plantIds = useMemo(() => {
    return plants.map((plant) => plant.plantId);
  }, [plants]);

  const { setNodeRef } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    },
  });

  return (
    <div ref={setNodeRef} className="w-full rounded-lg bg-slate-50 p-2">
      {/* Column title */}
      <h3 className="select-none rounded-sm bg-slate-100 p-2">
        <span className="rounded-md bg-slate-200 px-2 py-1">{plants?.length}</span>{' '}
        {column.title}
      </h3>

      {/* Column plant container */}
      <ScrollArea className="mt-2 flex grow flex-col gap-4">
        <SortableContext strategy={verticalListSortingStrategy} items={plantIds}>
          <div className="flex h-[400px] flex-col gap-2">
            {plants.map((plant) => (
              <PlantCard key={plant.plantId} plant={plant} />
            ))}
          </div>
        </SortableContext>
      </ScrollArea>
    </div>
  );
}

export function UserPlantsAccessManagementDesktop({
  plants,
  setPlants,
  isLoading,
}: {
  plants: TPlantWithAccessControl[];
  setPlants: React.Dispatch<React.SetStateAction<TPlantWithAccessControl[]>>;
  isLoading: boolean;
}) {
  const [columns, setColumns] = useState<TColumn[]>(defaultCols);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);

  const [activePlant, setActivePlant] = useState<TPlantWithAccessControl | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (isLoading)
    return (
      <div className="grid h-full max-h-[470px] w-full grid-cols-2 gap-4">
        <Skeleton />
        <Skeleton />
      </div>
    );

  return (
    <div className="flex w-full overflow-x-auto overflow-y-hidden">
      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <SortableContext items={columnsId}>
          <div className="grid flex-1 grid-cols-2 gap-4">
            {columns.map((col) => (
              <ColumnContainer
                key={col.id}
                column={col}
                plants={plants.filter((plant) => plant.columnId === col.id)}
              />
            ))}
          </div>
        </SortableContext>

        {createPortal(
          <DragOverlay>{activePlant && <PlantCard plant={activePlant} />}</DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === 'Plant') {
      setActivePlant(event.active.data.current.plant);
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActivePlant(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAColumn = active.data.current?.type === 'Column';
    if (isActiveAColumn) {
      setColumns((columns) => {
        const activeColumnIndex = columns.findIndex((col) => col.id === activeId);
        const overColumnIndex = columns.findIndex((col) => col.id === overId);

        return arrayMove(columns, activeColumnIndex, overColumnIndex);
      });
      return;
    }

    const isActiveAPlant = active.data.current?.type === 'Plant';
    const isOverAPlant = over.data.current?.type === 'Plant';

    if (!isActiveAPlant) return;

    if (isActiveAPlant && isOverAPlant) {
      setPlants((plants) => {
        const activeIndex = plants.findIndex((t) => t.plantId === activeId);
        const overIndex = plants.findIndex((t) => t.plantId === overId);

        if (plants[activeIndex].columnId != plants[overIndex].columnId) {
          plants[activeIndex].columnId = plants[overIndex].columnId;
          plants[activeIndex].newlyAdded = true;

          return arrayMove(plants, activeIndex, overIndex - 1);
        }

        return arrayMove(plants, activeIndex, overIndex);
      });
    }

    const isOverAColumn = over.data.current?.type === 'Column';

    if (isActiveAPlant && isOverAColumn) {
      setPlants((plants) => {
        const activeIndex = plants.findIndex((t) => t.plantId === activeId);

        plants[activeIndex].columnId = overId;
        plants[activeIndex].newlyAdded = true;

        return arrayMove(plants, activeIndex, activeIndex);
      });
    }
  }
}
